import React, { useState } from "react";
import { MessageSquarePlus, X, Loader2, Send } from "lucide-react";
import api, { formatError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";

const KINDS = [
  { v: "suggestion", label: "اقتراح" },
  { v: "recommendation", label: "توصية" },
  { v: "complaint", label: "شكوى" },
];

export default function FeedbackButton() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [kind, setKind] = useState("suggestion");
  const [message, setMessage] = useState("");
  const [email, setEmail] = useState("");
  const [sending, setSending] = useState(false);

  const emailValue = email || (user && user.email) || "";

  const submit = async () => {
    if (message.trim().length < 3) { toast.error("الرجاء كتابة رسالتك"); return; }
    if (!emailValue) { toast.error("الرجاء إدخال بريدك الإلكتروني للرد عليك"); return; }
    setSending(true);
    try {
      await api.post("/feedback", { kind, message: message.trim(), email: emailValue, name: user?.name || "" });
      toast.success("تم استلام رسالتك — سنرد عليك على بريدك قريباً 🙏");
      setOpen(false); setMessage(""); setEmail("");
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail) || "تعذّر الإرسال");
    } finally {
      setSending(false);
    }
  };

  if (typeof window !== "undefined" && window.self !== window.top) return null; // مخفي داخل نافذة عرض السياسات
  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className="fixed bottom-5 right-5 z-40 bg-accent hover:bg-accent/90 text-accent-foreground rounded-full shadow-lg h-11 w-11 sm:w-auto sm:h-12 sm:px-4 flex items-center justify-center gap-2 font-bold text-sm transition-transform hover:scale-105"
        aria-label="راسلنا" title="راسلنا"
        data-testid="feedback-open-btn"
      >
        <MessageSquarePlus className="w-5 h-5" /> <span className="hidden sm:inline">راسلنا</span>
      </button>

      {open && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/50 p-4" onClick={() => setOpen(false)}>
          <div className="bg-background border border-border rounded-2xl w-full max-w-md p-6 shadow-2xl" dir="rtl" onClick={(e) => e.stopPropagation()} data-testid="feedback-modal">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold">المقترحات والشكاوى</h3>
              <button onClick={() => setOpen(false)} className="text-muted-foreground hover:text-foreground" data-testid="feedback-close-btn"><X className="w-5 h-5" /></button>
            </div>
            <div className="flex gap-2 mb-3">
              {KINDS.map((k) => (
                <button key={k.v} onClick={() => setKind(k.v)}
                  className={`flex-1 h-9 rounded-lg text-sm font-semibold border transition ${kind === k.v ? "bg-primary text-white border-primary" : "bg-secondary border-border text-muted-foreground"}`}
                  data-testid={`feedback-kind-${k.v}`}>
                  {k.label}
                </button>
              ))}
            </div>
            <Textarea value={message} onChange={(e) => setMessage(e.target.value)} rows={4}
              placeholder="اكتب رسالتك هنا..." className="bg-secondary border-border mb-3" data-testid="feedback-message" />
            {!(user && user.email) && (
              <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                placeholder="بريدك الإلكتروني للرد عليك" dir="ltr" className="bg-secondary border-border text-start mb-3" data-testid="feedback-email" />
            )}
            <Button onClick={submit} disabled={sending} className="w-full gap-2 bg-primary hover:bg-primary/90 text-white font-bold" data-testid="feedback-submit-btn">
              {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} إرسال
            </Button>
            <p className="text-[11px] text-muted-foreground text-center mt-3">سنرد على بريدك من فريق رواج الذكية · support@rawajsmart.com</p>
          </div>
        </div>
      )}
    </>
  );
}
